import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

export type ProposalAuditCandidateRow = {
  candidate_id?: string;
  summary_label?: string;
  dimensions?: Record<string, unknown>;
  transaction_count?: number;
  sample_transaction_ids?: number[];
  sample_is_truncated?: boolean;
};

function parseCandidates(raw: unknown): ProposalAuditCandidateRow[] {
  if (!Array.isArray(raw)) return [];
  const out: ProposalAuditCandidateRow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const sample = Array.isArray(o.sample_transaction_ids)
      ? (o.sample_transaction_ids as unknown[]).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    out.push({
      candidate_id:
        typeof o.candidate_id === 'string'
          ? o.candidate_id
          : o.candidate_id != null
            ? String(o.candidate_id)
            : '',
      summary_label:
        typeof o.summary_label === 'string' ? o.summary_label : String(o.summary_label ?? '').slice(0, 400),
      dimensions: typeof o.dimensions === 'object' && o.dimensions !== null ? (o.dimensions as Record<string, unknown>) : {},
      transaction_count:
        typeof o.transaction_count === 'number' ? o.transaction_count : Number(o.transaction_count || 0),
      sample_transaction_ids: sample,
      sample_is_truncated: Boolean(o.sample_is_truncated)
    });
  }
  return out;
}

export { parseCandidates };

export type AuditProposalCandidatesModalKind =
  | 'classification'
  | 'supplier_alias'
  | 'vat_suggestion';

/** Selected bucket keys and merged sample transaction IDs for v2 generation. */
export type AuditProposalCandidatesGeneratePayload = {
  candidateIds: string[];
  unionTransactionIds: number[];
};

export type AuditProposalCandidatesModalProps = {
  open: boolean;
  title: string;
  auditKind: AuditProposalCandidatesModalKind;
  candidates: ProposalAuditCandidateRow[];
  generating: boolean;
  onClose: () => void;
  onGenerateSelected: (
    auditKind: AuditProposalCandidatesModalKind,
    payload: AuditProposalCandidatesGeneratePayload,
    truncatedSelected: boolean
  ) => void | Promise<void>;
};

/** Rules-tab modal: grouped audit buckets with checkboxes → union sample transaction IDs for draft generation. */
export function AuditProposalCandidatesModal(props: AuditProposalCandidatesModalProps): React.ReactElement {
  const {
    open,
    title,
    auditKind,
    candidates,
    generating,
    onClose,
    onGenerateSelected
  } = props;

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setSelected(new Set(candidates.map((c, i) => (c.candidate_id || '').trim() || `idx:${i}`)));
    }
  }, [open, candidates]);

  const keyed = useMemo(() => {
    return candidates.map((c, i) => ({
      row: c,
      key: (c.candidate_id || '').trim() || `idx:${i}`
    }));
  }, [candidates]);

  const selectedRows = keyed.filter(({ key }) => selected.has(key));
  const truncationWarning = selectedRows.some(({ row }) => row.sample_is_truncated);

  const allSelected = keyed.length > 0 && selectedRows.length === keyed.length;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(keyed.map(({ key }) => key)));
  };

  const toggleOne = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleGenerate = () => {
    const idSet = new Set<number>();
    const candidateIds: string[] = [];
    for (const { row, key } of keyed) {
      if (!selected.has(key)) continue;
      const cid = String(row.candidate_id ?? '').trim();
      if (cid) candidateIds.push(cid);
      for (const id of row.sample_transaction_ids || []) {
        if (Number.isFinite(id) && id > 0) idSet.add(id);
      }
    }
    void onGenerateSelected(
      auditKind,
      { candidateIds, unionTransactionIds: Array.from(idSet) },
      truncationWarning
    );
  };

  return (
    <Dialog open={open} onClose={generating ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {truncationWarning ? (
            <Alert severity="warning">
              One or more selected buckets only include sample transaction IDs; generation may not cover every
              transaction in those buckets. Use full ledger sweeps for complete coverage.
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            Select buckets, then generate drafts. Each row shows how many transactions match that bucket; only sample
            IDs are sent to the server.
          </Typography>
          <TableContainer sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" stickyHeader sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={buildFitColgroup([
                  { key: 'select', pct: 6 },
                  { key: 'label' },
                  { key: 'count', pct: 14 },
                  { key: 'sample', pct: 18 },
                ])}
              />
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedRows.length > 0 && !allSelected}
                      onChange={toggleAll}
                      disabled={generating || keyed.length === 0}
                      inputProps={{ 'aria-label': 'Select all buckets' }}
                    />
                  </TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Sample IDs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keyed.map(({ row, key }) => (
                  <TableRow key={key} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(key)}
                        onChange={() => toggleOne(key)}
                        disabled={generating}
                        inputProps={{ 'aria-label': `Select bucket ${row.summary_label || key}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx} title={row.summary_label || ''}>
                        {row.summary_label || '—'}
                      </Typography>
                      {row.sample_is_truncated ? (
                        <Typography variant="caption" color="warning.main" display="block">
                          Sample truncated
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{Number(row.transaction_count || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Box sx={fitTruncateSx} title={(row.sample_transaction_ids || []).join(', ')}>
                        {(row.sample_transaction_ids || []).length.toLocaleString()} id(s)
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={generating}>
          Close
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={handleGenerate} disabled={generating || selectedRows.length === 0}>
          {generating ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
